import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { loginSchema, TLoginSchema } from '@/schemas/auth/login.schema';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth, useLogin } from '@/hooks/auth.hooks';
import { AuthResponse } from '@/api/entities/auth';
import { logout } from '@/api/auth/auth';
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from '@/utils/token.utils';
import { clearStorage, clearLocalCache } from '@/utils/storage.utils';
import { AuthLayout } from '@/layouts/AuthLayout';
import { LoginManagement } from '../components/LoginManagement';
import { Loader } from '@/components/Loading/Loader';
import loginCarIllusationUrl from '@/assets/svg/login-car.svg';

export const Login = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [responseError, setResponseError] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(
    searchParams.get('logout') === 'true',
  );
  const { setLoggedInUser, loggedInUser } = useAuth();

  useEffect(() => {
    if (searchParams.get('logout') !== 'true') return;

    // Log out user if logout query exists and is true
    const performLogout = async () => {
      try {
        await logout();
      } catch {
        // Continue with local cleanup even if server logout fails
      }
      clearAccessToken();
      clearStorage();
      clearLocalCache();
      setLoggedInUser(undefined);
      searchParams.delete('logout');
      setSearchParams(searchParams, { replace: true });
      setIsLoggingOut(false);
    };

    performLogout();
  }, []);

  useEffect(() => {
    if (!isLoggingOut && getAccessToken()) {
      navigate('/');
    }
  }, [navigate, loggedInUser, isLoggingOut]);

  const {
    watch: watchLogin,
    control: loginControl,
    register: registerLogin,
    setValue: setLoginValue,
    setError: setLoginError,
    handleSubmit: handleSubmitLogin,
    formState: { errors: loginErrors, isSubmitting: isSubmittingLogin },
  } = useForm<TLoginSchema>({
    resolver: zodResolver(loginSchema),
  });

  const { mutate: mutateLogin, isPending: isPendingLogin } = useLogin({
    handleSuccess: (data: AuthResponse) => {
      setAccessToken(data?.data?.access_token);
      setLoggedInUser(data?.data?.user);
      navigate('/');
    },
    handleError: (error) => {
      if (error?.status === 404 || error?.error === 'Not Found') {
        setResponseError(t('emailOrPasswordIncorrect'));
      } else {
        setResponseError(t('common:errorOccurredTryAgainLater'));
      }
    },
  });

  const onSubmit = (credentials: TLoginSchema) => {
    mutateLogin(credentials);
  };

  return (
    <>
      <AuthLayout
        illustrationUrl={loginCarIllusationUrl}
        title={t('welcome')}
        description={t('loginToDashboard')}
      >
        <LoginManagement<TLoginSchema>
          t={t}
          responseError={responseError}
          setResponseError={setResponseError}
          watch={watchLogin}
          control={loginControl}
          register={registerLogin}
          setValue={setLoginValue}
          setError={setLoginError}
          errors={loginErrors}
          submitDisabled={isSubmittingLogin || isPendingLogin}
          onSubmit={handleSubmitLogin(onSubmit)}
        />
      </AuthLayout>
      {(isSubmittingLogin || isPendingLogin) && <Loader />}
    </>
  );
};
